import '../CopNotifications/CopNotifications.scss';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type AppNotification, fetchNotifications, fetchUnreadCount, markAllNotificationsAsRead, markAsRead, type NotificationCategory, } from '../../services/notification'
import { ToastService } from '../../services/toast.service';
import { NotificationsAccessGuard, useNotificationCategories } from '../Shared/NotificationsAccessGuard';
import { CATEGORY_INNER_TAB_ID, INNER_TAB_ID_CATEGORY_MAP, PAGE_SIZE } from './notificationConstants';
import NotificationItem from "./NotificationItem";
import NotificationSettings from './NotificationSettings';
import { type CategoryBrowserSetting, GROUP_ORDER, type GroupKey, groupNotifications } from './notificationUtils';
import { ResolutionConfigContext } from './ResolutionConfigContext';
import SignalRProvider, { useSignalR } from './SignalR/SignalRProvider';
import Tabs, { type Tab } from './Tabs';

export type CopNotificationProps = {
  notificationsConfig?: string
};

// Stable fallback reference so unauthored config doesn't create a new object identity on every render
const EMPTY_RECORD: Record<string, never> = {};

const CopNotificationsInner = ({ notificationsConfig }: CopNotificationProps) => {
  // Memoized so downstream useCallback deps don't churn on every render
  const notificationData = useMemo(
    () => (notificationsConfig ? JSON.parse(notificationsConfig) : null),
    [notificationsConfig],
  );
  const panelTitle = notificationData?.title ?? 'Notifications';
  const template = notificationData ? notificationData.templates : null;
  const defaultCategoryTitle = notificationData ? notificationData.categories : null;
  const all = notificationData ? notificationData.all : 'All';
  const unRead = notificationData ? notificationData.unread : 'UnRead';
  const markAllAsReadLabel = notificationData?.markAllAsRead ?? 'Mark all as read';
  const links = notificationData?.links ? notificationData.links : null;
  const GROUP_LABELS: Record<GroupKey, string> = notificationData ? notificationData.grouping : EMPTY_RECORD;
  const resolutionConfig = notificationData?.resolutionConfig ?? null;
  const settingsIconLabel = notificationData?.settingsIconLabel ?? 'Notification settings';
  const backButtonLabel = notificationData?.backButtonLabel ?? 'Back';
  const elapsedTimeLabels = notificationData?.elapsedTime ?? EMPTY_RECORD;
  const statusMessages = notificationData?.status ?? EMPTY_RECORD;
  const errorMessages = notificationData?.errors ?? EMPTY_RECORD;
  const [showSettings, setShowSettings] = useState(false);
  const [category, setCategory] = useState<NotificationCategory>('All');
  const [isUnreadTab, setIsUnreadTab] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [showHideSetting, setShowHideSetting] = useState<CategoryBrowserSetting[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const { onNotification } = useSignalR();

  const allCategorySetting = showHideSetting.find((s: CategoryBrowserSetting) => s?.category === 'All');
  const isBrowserEnabledForAll: boolean = allCategorySetting?.browserEnabled ?? true;
  const currentCategorySetting = showHideSetting.find((s: CategoryBrowserSetting) => s?.category === category);
  const isBrowserEnabledForCurrentCategory: boolean = currentCategorySetting?.browserEnabled ?? true;
  const popoverRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      const isInsideModal = target.closest?.('.rejection-modal-overlay, .cancel-confirm-modal-overlay');
      if (containerRef.current && !containerRef.current.contains(target) && !isInsideModal) {
        setIsOpen(false);
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    fetchUnreadCount().then(setUnreadCount)
  }, []);

  // Refresh unread count when a real-time notification arrives via SignalR
  useEffect(() => {
    return onNotification(() => {
      fetchUnreadCount().then(setUnreadCount)
    })
  }, [onNotification]);

  const loadNotifications = useCallback(async (cat: NotificationCategory, unreadOnly: boolean, failureMessage?: string) => {
    setLoading(true)
    setError(null)
    const result = await fetchNotifications({ category: cat, unreadOnly, top: PAGE_SIZE, skip: 0 })
    if (result) {
      setNotifications(result.value)
      setTotalCount(result['@odata.count'] ?? 0)
    } else {
      setError(failureMessage ?? statusMessages?.error?.title ?? 'Failed to load notifications.')
    }
    setLoading(false)
  }, [statusMessages]);

  const handleMarkAllAsRead = useCallback(async () => {
    setMarkingAllRead(true)
    const success = await markAllNotificationsAsRead()
    if (success) {
      setUnreadCount(0)
      loadNotifications(category, isUnreadTab, errorMessages?.markAllAsReadLoading)
    } else {
      ToastService.error(errorMessages?.markAllAsRead ?? 'Failed to mark all notifications as read.')
    }
    setMarkingAllRead(false)
  }, [category, isUnreadTab, loadNotifications, errorMessages])

  const loadMoreNotifications = useCallback(async (
    cat: NotificationCategory,
    unreadOnly: boolean,
    currentSkip: number,
  ) => {
    setLoadingMore(true)
    const result = await fetchNotifications({ category: cat, unreadOnly, top: PAGE_SIZE, skip: currentSkip })
    if (result) {
      setNotifications(prev => [...prev, ...result.value])
      setTotalCount(result['@odata.count'] ?? 0)
    }
    setLoadingMore(false)
  }, [])

  useEffect(() => {
    loadNotifications(category, isUnreadTab)
  }, [category, isUnreadTab, loadNotifications,isOpen])

  const stateRef = useRef({ category, isUnreadTab, notifications, totalCount, loading, loadingMore })
  useEffect(() => {
    stateRef.current = { category, isUnreadTab, notifications, totalCount, loading, loadingMore }
  })

  useEffect(() => {
    if (!isOpen) return
    const el = listRef.current
    if (!el) return
    const handleScroll = () => {
      const { category: cat, isUnreadTab: unreadOnly, notifications: items, totalCount: total, loading: l, loadingMore: lm } = stateRef.current
      if (l || lm) return
      if (items.length >= total) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
        loadMoreNotifications(cat, unreadOnly, items.length)
      }
    }
    el.addEventListener('scroll', handleScroll)
    return () => el.removeEventListener('scroll', handleScroll)
  }, [isOpen, loadMoreNotifications])

  const handleInnerTabChange = (tab: Tab) => {
    const mapped = INNER_TAB_ID_CATEGORY_MAP[tab.id] ?? 'All'
    setCategory(mapped)
  }

  const handleMainTabChange = (tab: Tab) => {
    setIsUnreadTab(tab.id === 2)
  }

  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    const success = await markAsRead(notificationId)
    if (success) {
      fetchUnreadCount().then(setUnreadCount)
      loadNotifications(category, isUnreadTab)
    } else {
      ToastService.error(errorMessages?.markAsRead ?? 'Failed to mark notification as read.')
    }
  }, [category, isUnreadTab, loadNotifications, errorMessages])

  const renderGrouped = (items: AppNotification[]) => {
    const groups = groupNotifications(items);
    return GROUP_ORDER.filter(key => groups[key]?.length).map(key => (
      <div key={key} className="cop-notification__group">
        <div className="cop-notification__group-label">{GROUP_LABELS[key]}</div>
        {groups[key]!.map(n => <NotificationItem key={n.id} notification={n} notificationTexts={template} links={links} elapsedTimeLabels={elapsedTimeLabels} unreadLabel={unRead} onMarkAsRead={handleMarkAsRead} />)}
      </div>
    ));
  }

  const renderContent = () => {
    if (loading) return (
      <div className="cop-notification__loading">
        <span className="cop-notification__spinner" />
      </div>
    )
    if (error) return <div className="cop-notification__error">{error}</div>
    if (!notifications.length) return <div className="cop-notification__empty">{statusMessages?.empty?.title ?? 'No notifications'}</div>
    return (
      <>
        {renderGrouped(notifications)}
        {loadingMore && (
          <div className="cop-notification__loading-more">
            <span className="cop-notification__spinner cop-notification__spinner--sm" />
          </div>
        )}
      </>
    )
  }

  const renderDisabledBanner = () => (
    <div className="cop-notification__disabled-banner">
      <div className="cop-notification__disabled-banner-icon">
        <span className="cop-notification__disabled-bell-icon" aria-hidden="true" />
      </div>
      <h4 className="cop-notification__disabled-banner-title">{statusMessages?.disabled?.title ?? 'Notifications Disabled'}</h4>
      <p className="cop-notification__disabled-banner-desc">{statusMessages?.disabled?.description ?? 'You currently have notifications disabled. To start receiving update again, please adjust your in-app notification settings.'}</p>
    </div>
  );

  const apiCategories = useNotificationCategories()

  // Build allowed tab ids from API categories. "All" tab (id:1) is always included.
  const allowedTabIds = new Set<number>([
    1, // "All" tab always visible
    ...apiCategories
      .filter(c => c.id !== 0) // exclude the "All" meta-entry (id:0) from API
      .map(c => CATEGORY_INNER_TAB_ID[c.name as NotificationCategory])
      .filter((id): id is number => id !== undefined),
  ])

  const ALL_INNER_TABS: Tab[] = [
    { id: 1, label: defaultCategoryTitle?.all,       content: null },
    { id: 2, label: defaultCategoryTitle?.cases,      content: null },
    { id: 3, label: defaultCategoryTitle?.agreements, content: null },
    { id: 4, label: defaultCategoryTitle?.assets,     content: null },
  ]
  const innerTabs = ALL_INNER_TABS.filter(tab => allowedTabIds.has(tab.id))
  const mainTabs: Tab[] = [
    { id: 1, label: all, content: null },
    { id: 2, label: unRead, content: null },
  ]
  return (
    <div className="cop-notification" ref={containerRef}>
      <div className="cop-notification__bell-container">
        <button className="cop-notification__bell-btn" type="button" onClick={() =>{ const opening = !isOpen;
          setIsOpen(opening);
          if (!opening) setShowSettings(false);} }>
          {isBrowserEnabledForAll ? (
            <>
              <span className="cop-notification__bell-icon" aria-hidden="true" />
              {unreadCount > 0 && (
                <span className={`cop-notification__badge${unreadCount <= 9 ? ' cop-notification__badge--single' : ''}`}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </>
          ) : (
            <span className="cop-notification__bell-muted-icon" aria-hidden="true" />
          )}
        </button>
        {isOpen && (
          <div className="cop-notification__popover" ref={popoverRef}>
            <ResolutionConfigContext.Provider value={resolutionConfig}>
              {showSettings ? (
                <NotificationSettings
                  onBack={() => setShowSettings(false)}
                  settingsConfig={notificationData?.settings}
                  categoryLabels={notificationData?.categories}
                  backButtonLabel={backButtonLabel}
                  loadErrorMessage={statusMessages?.settingsLoadError}
                  updateErrorMessage={statusMessages?.settingsUpdateError}
                  setShowHideSetting={setShowHideSetting}
                />
              ) : (
                <>
                  <div className="cop-notification__popover-header-wrapper">
                    <h3 className="cop-notification__popover-header">{panelTitle}</h3>
                    {unreadCount > 0 &&
                      <button
                        className="cop-notification__mark-all-read-btn"
                        type="button"
                        onClick={handleMarkAllAsRead}
                        disabled={markingAllRead}
                      >
                        {markingAllRead && <span className="cop-notification__mark-all-spinner" />}
                        {markAllAsReadLabel}
                      </button>}
                    <button
                      className="cop-notification__settings-btn"
                      title={settingsIconLabel}
                      aria-label={settingsIconLabel}
                      onClick={() => setShowSettings(true)}
                      type="button"
                    >
                      <span className="cop-notification__settings-icon" aria-hidden="true" />
                    </button>
                  </div>
                  <Tabs tabs={mainTabs} onTabChange={handleMainTabChange} />
                  <Tabs tabs={innerTabs} variant="inner" activeTab={CATEGORY_INNER_TAB_ID[category]} onTabChange={handleInnerTabChange} />
                  {!isBrowserEnabledForCurrentCategory && renderDisabledBanner()}
                  <div className="cop-notification__list" ref={listRef}>{renderContent()}</div>
                </>
              )}
            </ResolutionConfigContext.Provider>
          </div>
        )}
      </div>
    </div>
  );
};

const parseAccessGuardErrorMessage = (notificationsConfig?: string): string | undefined => {
  try {
    return notificationsConfig ? JSON.parse(notificationsConfig)?.errors?.accessGuardError : undefined;
  } catch {
    return undefined;
  }
};

const CopNotifications = (props: CopNotificationProps) => (
  <NotificationsAccessGuard errorMessage={parseAccessGuardErrorMessage(props.notificationsConfig)}>
    <SignalRProvider>
      <CopNotificationsInner {...props} />
    </SignalRProvider>
  </NotificationsAccessGuard>
);

export default CopNotifications;